import { useCallback } from 'react';
import { Vector3 } from 'three';
import { isApartmentAvailable as checkAvailability } from "../utils/apartmentStatus";

/**
 * Custom hook for managing apartment nodes
 * @param {Object} params - Parameters
 * @param {React.RefObject} params.apartmentNodes - Apartment nodes ref
 * @param {React.RefObject} params.vacantColor - Vacant color material ref
 * @param {React.RefObject} params.occupiedColor - Occupied color material ref
 * @param {React.RefObject} params.vacantHoveredColor - Vacant hovered color material ref
 * @param {React.RefObject} params.occupiedHoveredColor - Occupied hovered color material ref
 * @param {React.RefObject} params.appRef - Current app ref
 * @returns {Object} Apartment management functions
 */
export const useApartmentNodes = ({
  apartmentNodes,
  vacantColor,
  occupiedColor,
  vacantHoveredColor,
  occupiedHoveredColor,
  appRef,
}) => {

  const isApartmentAvailable = useCallback((apartment) => checkAvailability(apartment), []);

  const setAllApartmentsNodes = useCallback((ids = []) => {
    // Add current app id if exists
    if (!apartmentNodes || apartmentNodes.length === 0) return;
    if (appRef.current) {
      ids.push(appRef.current.id);
    }
    // console.log(apartmentNodes)

    apartmentNodes.forEach((node) => {
      if (!node || !node.userData.apartment) return;

      const apartment = node.userData.apartment;
      const isHovered = ids.includes(apartment.id);
      const isAvailable = isApartmentAvailable(apartment);

      if (isHovered) {
        // Apply hovered material
        // console.log("SET HOVERED MATERIAL", isAvailable, vacantHoveredColor.current);
        node.material = isAvailable 
          ? vacantHoveredColor.current 
          : occupiedHoveredColor.current;
      } else {
        // Apply normal material
        node.material = isAvailable 
          ? vacantColor.current 
          : occupiedColor.current;
      }
    });
  }, [apartmentNodes, vacantColor, occupiedColor, vacantHoveredColor, occupiedHoveredColor, appRef, isApartmentAvailable]);

  const findApartmentNode = useCallback((apartmentId) => {
    if (!apartmentNodes) return null;
    
    return apartmentNodes.find(
      (node) => node.userData.apartment?.id === apartmentId
    );
  }, [apartmentNodes]);

  const setApartmentVisibility = useCallback((apartmentId, visible) => {
    const node = findApartmentNode(apartmentId);
    if (node) {
      node.visible = visible;
    }
  }, [findApartmentNode]);

  const setAllApartmentsVisibility = useCallback((visible) => {
    if (!apartmentNodes) return;
    
    apartmentNodes.forEach((node) => {
      node.visible = visible;
    });
  }, [apartmentNodes]);

  const getApartmentWorldPosition = useCallback((apartmentId) => {
    const node = findApartmentNode(apartmentId);
    if (node) {
      const worldPosition = new Vector3();
      node.getWorldPosition(worldPosition);
      return worldPosition;
    }
    return null;
  }, [findApartmentNode]);

  return {
    setAllApartmentsNodes,
    findApartmentNode,
    setApartmentVisibility,
    setAllApartmentsVisibility,
    getApartmentWorldPosition,
    isApartmentAvailable,
  };
};
